import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { ApplicationConfigService } from '../core/config/application-config.service';
import { Login } from './login/login.model';
import { AlertService } from '../shared/services/alert.service';
import { CookieService } from 'ngx-cookie';

type JwtToken = {
  token: string;
};

@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private loginResponseSubject = new BehaviorSubject<any>(null);
  loginResponse$: Observable<any> = this.loginResponseSubject.asObservable();

  resourceUrl;

  constructor(
    private http: HttpClient,
    private alertService: AlertService,
    private cookieService: CookieService,
    private localStorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private applicationConfigService: ApplicationConfigService,
  ) {
    this.resourceUrl = this.applicationConfigService.getEndpointFor('account-portal/auth');
  }

  getToken(): string {
    const tokenInLocalStorage: string | null = this.localStorageService.retrieve('authtoken');
    const tokenInSessionStorage: string | null = this.sessionStorageService.retrieve('authtoken');
    const tokenCookieStorage: string | null | any = this.cookieService.get('authtoken');
    return tokenInLocalStorage ?? tokenInSessionStorage ?? tokenCookieStorage ?? '';
  }

  login(credentials: Login): Observable<any> {
    return this.http.post<any>(`${this.resourceUrl}/login`, credentials).pipe(
      map((response) => {
        this.localStorageService.store('scope', response.scope);
        if (response.userStatus == 'FORCE_PASSWORD_CHANGED') {
          this.localStorageService.store('userStatus', response.userStatus);
          this.localStorageService.store('authtoken', response.accessToken);
        } else if (response.responseCode == 'EU403') {
          this.alertService.showCommonAlertMessage(response.responseMessage, response.responseCode);
        } else if (response?.accessToken && response.accessToken !== null) {
          localStorage.removeItem('userStatus');
          this.authenticateSuccess(response.accessToken, credentials.rememberMe);
          this.loginResponseSubject.next(response.scope);
        } else {
          localStorage.removeItem('userStatus');
          this.alertService.showCommonAlertMessage(response.responseMessage, response.responseCode);
        }
      }),
    );
  }

  logout(): Observable<void> {
    return new Observable((observer) => {
      this.http.delete(`${this.resourceUrl}/logout`);
      this.localStorageService.clear('authtoken');
      this.sessionStorageService.clear('authtoken');
      this.cookieService.remove('authtoken');
      observer.complete();
    });
  }

  private authenticateSuccess(token: string, rememberMe?: boolean): void {
    this.localStorageService.store('authtoken', token);
    this.sessionStorageService.store('authtoken', token);
  }
}
